import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Divider, IconButton, List, ListItem, SwipeableDrawer, Typography } from '@mui/material';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import CloseIcon from '@mui/icons-material/Close';
// Functions/Constants/Enum
import HttpService from 'shared/services/Http.service';
import { API_CONFIG } from 'shared/constants/api';
import { ROUTES } from 'shared/constants/constant';
import { colors } from 'assets/styles/AMBGTheme';
import { createAction } from 'shared/util/Utility';
import * as actionTypes from 'store/action-types';
// Components
import AMBGInfiniteScroll from 'shared/components/AMBGInfiniteScroll/AMBGInfiniteScroll';
import AMBGLoader from 'shared/components/AMBGLoader/AMBGLoader';
import AMBGTooltip from 'shared/components/AMBGToolTip/AMBGToolTip';
// Styles
import * as Sx from './Notification.styles';
// Type/Interface
import { IAuthState, IState } from 'shared/interface/state';
import { IRole } from 'features/auth/login/LoginScreen.type';

type INotificationItem = {
	notification_id: string;
	action_type: string;
	type: string;
	notification: string;
	created_at: string;
	role: string;
	role_id: number;
	company: string;
	is_read: boolean;
};
type NotificationProps = {
	isOpen: boolean;
	handleNotificationToggle: () => void;
	handleUnreadCount: (count: number) => void;
	unreadCount: number;
};

const Notification: FC<NotificationProps> = (props) => {
	const { isOpen, handleNotificationToggle, handleUnreadCount, unreadCount } = props;
	const isCallAPI = useSelector((state: IState) => state.notification.isCallAPI);
	const role = useSelector((state: IState) => state.auth.role as IRole);
	const authData = useSelector((state: IState) => state.auth as IAuthState);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [notificationList, setNotificationList] = useState<INotificationItem[]>([]);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [excelActionLoading, setExcelActionLoading] = useState<boolean>(false);
	const [paginationParams, setPaginationParams] = useState({
		currentPage: 1,
		total: 0
	});

	const isSuperOrAmbgAdmin = role.role_id === 1 || role.role_id === 5;

	useEffect(() => {
		setIsDrawerOpen(isOpen);
	}, [isOpen]);

	const fetchNotificationList = useCallback(
		async (page = paginationParams.currentPage, isRequiredOldData = false) => {
			try {
				setLoading(true);
				const params = {
					current_page: page,
					record_per_page: 25
				};
				const response = await HttpService.get(API_CONFIG.notification, params);
				const { data, pagination_data, unread_count } = response.data;
				handleUnreadCount(unread_count);
				if (isRequiredOldData) {
					setNotificationList((list) => [...list, ...data]);
				} else {
					setNotificationList(data);
				}
				setPaginationParams({ currentPage: pagination_data.current_page, total: pagination_data.total });
				setLoading(false);
				dispatch(createAction(actionTypes.NOTIFICATION_API_CALL, { isCallAPI: false }));
			} catch (error) {
				console.error(error);
				setLoading(false);
			}
		},
		[dispatch, handleUnreadCount, paginationParams.currentPage]
	);
	const markAsReadNotification = useCallback(async () => {
		try {
			setLoading(true);

			const response = await HttpService.post(API_CONFIG.markAllAsRead);
			if (!response.is_error) {
				fetchNotificationList();
			}
			setLoading(false);
		} catch (error) {
			console.error(error);
			setLoading(false);
		}
	}, [fetchNotificationList]);

	const downloadNotifications = useCallback(async () => {
		try {
			setExcelActionLoading(true);
			const response = await HttpService.get(
				API_CONFIG.downloadNotificationsExcel,
				{},
				{ responseType: 'arraybuffer' }
			);

			const fileStartDate = moment(authData.sales_end_date)
				.subtract(1, 'month')
				.add(1, 'days')
				.format('DD-MMM-YYYY');
			const fileEndDate = moment(authData.sales_end_date).format('DD-MMM-YYYY');

			const blob = new Blob([response], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `AMBG - Notifications Log - ${fileStartDate} to ${fileEndDate}.xlsx`;
			link.click();
			setExcelActionLoading(false);
		} catch (error) {
			console.error(error);
			setExcelActionLoading(false);
		}
	}, []);

	const handleNotification = (type: string, id: string, action: string) => {
		let route = '';
		if (type === 'master') {
			if (action.includes('create')) {
				route = ROUTES.MASTER_CREATE_CHANGE_LOG;
			} else if (action.includes('update')) {
				route = ROUTES.MASTER_UPDATE_CHANGE_LOG;
			} else if (action.includes('delete')) {
				route = ROUTES.MASTER_DELETE_CHANGE_LOG;
			}
		} else if (type === 'sales' && ['download excel', 'download pdf'].includes(action) === false) {
			route = action.includes('create') ? ROUTES.SALES_CREATE_CHANGE_LOG : ROUTES.SALES_UPDATE_CHANGE_LOG;
		}

		if (route) {
			handleNotificationToggle();
			navigate(`${route}/${id}`);
			setIsDrawerOpen(false);
		}
	};

	const handleReadNotification = async (id: string) => {
		try {
			await HttpService.post(`${API_CONFIG.readNotification}/${id}`).then(() => {
				const updatedNotification = [...notificationList];
				updatedNotification.map((item) => {
					if (item.notification_id === id) {
						item.is_read = true;
					}
				});
				handleUnreadCount(unreadCount - 1);
				setNotificationList(updatedNotification);
			});
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		fetchNotificationList();
	}, []);

	useEffect(() => {
		if (isCallAPI) fetchNotificationList();
	}, [fetchNotificationList, isCallAPI]);

	return (
		<SwipeableDrawer
			anchor='right'
			open={isDrawerOpen}
			PaperProps={{ sx: { width: '500px' } }}
			onClose={handleNotificationToggle}
			onOpen={handleNotificationToggle}
		>
			<Box sx={Sx.notificationTitleBox}>
				<Typography sx={Sx.notificationTitle} data-testid='notificationTitle'>
					Notifications
				</Typography>
				<AMBGTooltip
					title='Close'
					arrow
					PopperProps={{
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: [0, 0]
								}
							}
						]
					}}
				>
					<IconButton onClick={handleNotificationToggle}>
						<CloseIcon />
					</IconButton>
				</AMBGTooltip>
			</Box>
			<Divider />
			{isDrawerOpen && (
				<>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: isSuperOrAmbgAdmin ? 'space-between' : 'end',
							margin: '7px 25px'
						}}
					>
						{isSuperOrAmbgAdmin && (
							<Typography
								sx={{
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: '600',
									color: colors.primary.main
								}}
								onClick={() => !excelActionLoading && downloadNotifications()}
							>
								Download Notifications for Previous Month
							</Typography>
						)}
						<Typography
							sx={{
								cursor: unreadCount > 0 ? 'pointer' : 'initial',
								fontSize: '12px',
								fontWeight: '600',
								color: unreadCount > 0 ? colors.primary.main : colors.secondary.light
							}}
							onClick={() => unreadCount > 0 && markAsReadNotification()}
						>
							Mark all as read
						</Typography>
					</Box>
					<AMBGInfiniteScroll
						fetchData={() => fetchNotificationList(paginationParams.currentPage + 1, true)}
						totalData={notificationList.length}
						hasMore={notificationList.length < paginationParams.total}
						height='calc(100vh - 100px)'
						endMessage={'Yay! You have seen it all'}
						isSpinner={loading}
					>
						<List sx={{ padding: '0 20px' }}>
							{loading && <AMBGLoader />}
							{!loading && isEmpty(notificationList) && (
								<Typography textAlign='center' mt='30px'>
									No data found
								</Typography>
							)}
							{notificationList.length > 0 &&
								notificationList.map((item, index: number) => (
									<ListItem
										key={index}
										disablePadding
										sx={{
											...Sx.listItemStyle,
											background: colors.secondary.shade4
										}}
										data-testid='notificationListItem'
										onClick={() => {
											handleNotification(item.type, item.notification_id, item.action_type);
											!item.is_read && handleReadNotification(item.notification_id);
										}}
									>
										<Typography
											sx={{ wordBreak: 'break-word' }}
											fontSize='14px'
											dangerouslySetInnerHTML={{ __html: item.notification }}
										/>
										{!item.is_read && (
											<Box
												component={'span'}
												sx={{
													height: '8px',
													width: '8px',
													borderRadius: '50%',
													background: colors.primary.main,
													position: 'absolute',
													top: '8px',
													right: '8px'
												}}
											/>
										)}
										<Box sx={Sx.notificationBottomText} data-testid='notification-bottom-text'>
											<Typography fontSize='12px' fontWeight='600'>
												{item.role} {item.role_id !== 1 && `of ${item.company}`}
											</Typography>
											<Typography fontSize='12px'>
												{moment(item.created_at).format('dddd, DD MMMM YYYY')}
											</Typography>
										</Box>
									</ListItem>
								))}
						</List>
					</AMBGInfiniteScroll>
				</>
			)}
		</SwipeableDrawer>
	);
};

export default Notification;
